import { Switch, Route, useLocation, Redirect } from "wouter";
import { useEffect } from "react";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as SonnerToaster } from "sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { RouteGuard } from "@/components/RouteGuard";
import { AuthProvider } from "@/hooks/useAuth";
import { ThemeProvider } from "@/components/ThemeProvider";

function ScrollToTop() {
  const [location] = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);
  
  return null;
}
import NotFound from "@/pages/not-found";
import Home from "@/pages/home";
import InterviewComplete from "@/pages/interview-complete";
import CandidateDossier from "@/pages/candidate-dossier";
import AgentDashboard from "@/pages/agent-dashboard";
import CreateRole from "@/pages/create-role";
import JobDetail from "@/pages/job-detail";
import Login from "@/pages/login";
import Register from "@/pages/register";
import AuthVerify from "@/pages/auth-verify";
import AdminLogin from "@/pages/admin-login";
import Contact from "@/pages/contact";
import About from "@/pages/about";
import SharedDossier from "@/pages/shared-dossier";
import InterviewInvite from "@/pages/interview-invite";
import Privacy from "@/pages/privacy";
import Terms from "@/pages/terms";
import Methodology from "@/pages/methodology";
import ProcessControls from "@/pages/process-controls";
import Deliverables from "@/pages/deliverables";
import ComplianceDataHandling from "@/pages/compliance-data-handling";
import DocumentLibrary from "@/pages/document-library";
import ChangeLog from "@/pages/change-log";
import UserGuide from "@/pages/user-guide";
import MotherDashboard from "@/pages/mother-dashboard";
import OpsLogin from "@/pages/ops-login";
import OpsVerify from "@/pages/ops-verify";
import Security from "@/pages/security";
import Services from "@/pages/services";
import IncidentResponse from "@/pages/incident-response";
import Partners from "@/pages/partners";
import HowItWorks from "@/pages/how-it-works";
import SettingsIntegrations from "@/pages/settings-integrations";
import SettingsCommunications from "@/pages/settings-communications";
import SettingsBilling from "@/pages/settings-billing";
import Pricing from "@/pages/pricing";
import Start from "@/pages/start";
import JobadderCandidate from "@/pages/jobadder-candidate";

function Router() {
  return (
    <Switch>
      {/* Public Routes */}
      <Route path="/" component={Home} />
      <Route path="/login" component={Login} />
      <Route path="/register" component={Register} />
      <Route path="/auth/verify" component={AuthVerify} />
      <Route path="/admin/login" component={AdminLogin} />
      <Route path="/ops" component={OpsLogin} />
      <Route path="/ops/verify" component={OpsVerify} />
      <Route path="/interview-complete" component={InterviewComplete} />
      <Route path="/services" component={Services} />
      <Route path="/contact" component={Contact} />
      <Route path="/about" component={About} />
      <Route path="/privacy" component={Privacy} />
      <Route path="/terms" component={Terms} />
      <Route path="/methodology" component={Methodology} />
      <Route path="/process-controls" component={ProcessControls} />
      <Route path="/deliverables" component={Deliverables} />
      <Route path="/compliance-data-handling" component={ComplianceDataHandling} />
      <Route path="/document-library" component={DocumentLibrary} />
      <Route path="/change-log" component={ChangeLog} />
      <Route path="/security" component={Security} />
      <Route path="/user-guide" component={UserGuide} />
      <Route path="/partners" component={Partners} />
      <Route path="/how-it-works" component={HowItWorks} />
      <Route path="/pricing" component={Pricing} />
      <Route path="/start" component={Start} />
      <Route path="/shared/:token" component={SharedDossier} />
      <Route path="/jobadder/candidate" component={JobadderCandidate} />
      
      {/* Single-Use Interview Invite Route (no auth required) */}
      <Route path="/interview/:token" component={InterviewInvite} />
      
      {/* Redirect removed routes */}
      <Route path="/jobs">
        {() => <Redirect to="/login" />}
      </Route>
      <Route path="/jobs/:id">
        {() => <Redirect to="/login" />}
      </Route>

      <Route path="/apply">
        {() => <Redirect to="/login" />}
      </Route>
      
      {/* Protected: Agent Routes Only */}
      <Route path="/dashboard">
        {() => <Redirect to="/agent/dashboard" />}
      </Route>
      <Route path="/agent">
        {() => <Redirect to="/agent/dashboard" />}
      </Route>
      <Route path="/agent/dashboard">
        <RouteGuard allowedRoles={["RECRUITMENT_AGENT", "ADMIN"]}>
          <AgentDashboard />
        </RouteGuard>
      </Route>
      <Route path="/agent/create-job">
        <RouteGuard allowedRoles={["RECRUITMENT_AGENT", "ADMIN"]}>
          <CreateRole />
        </RouteGuard>
      </Route>
      <Route path="/agent/jobs/:jobId">
        {(params) => (
          <RouteGuard allowedRoles={["RECRUITMENT_AGENT", "ADMIN"]}>
            <JobDetail />
          </RouteGuard>
        )}
      </Route>
      <Route path="/dossier/:applicationId">
        {(params) => (
          <RouteGuard allowedRoles={["RECRUITMENT_AGENT", "ADMIN"]}>
            <CandidateDossier />
          </RouteGuard>
        )}
      </Route>
      
      {/* Mother Dashboard - Control Plane — ADMIN only.
          Unauthenticated users are sent to /ops, not /login. */}
      <Route path="/mother">
        <RouteGuard allowedRoles={["ADMIN"]} loginRedirect="/ops">
          <MotherDashboard />
        </RouteGuard>
      </Route>
      <Route path="/mother/:tab">
        <RouteGuard allowedRoles={["ADMIN"]} loginRedirect="/ops">
          <MotherDashboard />
        </RouteGuard>
      </Route>
      
      {/* Integration Settings */}
      <Route path="/settings/integrations">
        <RouteGuard allowedRoles={["RECRUITMENT_AGENT", "ADMIN"]}>
          <SettingsIntegrations />
        </RouteGuard>
      </Route>

      {/* Communication Settings */}
      <Route path="/settings/communications">
        <RouteGuard allowedRoles={["RECRUITMENT_AGENT", "ADMIN"]}>
          <SettingsCommunications />
        </RouteGuard>
      </Route>

      {/* Billing Settings */}
      <Route path="/settings/billing">
        <RouteGuard allowedRoles={["RECRUITMENT_AGENT", "ADMIN"]}>
          <SettingsBilling />
        </RouteGuard>
      </Route>

      {/* Internal Admin Documents */}
      <Route path="/internal/incident-response">
        <RouteGuard allowedRoles={["RECRUITMENT_AGENT", "ADMIN"]}>
          <IncidentResponse />
        </RouteGuard>
      </Route>

      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TooltipProvider>
            <ScrollToTop />
            <Toaster />
            <SonnerToaster richColors position="top-right" />
            <Router />
          </TooltipProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
